import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import apiCall from "auth/ApiCallInstance";
import { USER_INFO } from "constants/AuthConstant";

const Ctx = createContext(null);
export const useAdminNotifications = () => useContext(Ctx);

// Helper to check if user is admin
const isAdminUser = () => {
  try {
    const userStr = localStorage.getItem(USER_INFO);
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    return user?.role === 'admin' || user?.role === 'super_admin';
  } catch (e) {
    return false;
  }
};

export function AdminNotificationsProvider({ children }) {
  const [items, setItems] = useState([]);

  const fetchLatest = useCallback(async () => {
    // Only fetch for admin/super_admin users
    if (!isAdminUser()) {
      return;
    }
    
    try {
      const r = await apiCall.get("/admin-notifications?limit=50");
      setItems(r.data || []);
    } catch (e) {
      // Silently fail if not authorized
      if (e.response?.status === 403) {
        console.log('[AdminNotifications] User not authorized to view admin notifications');
        return;
      }
      console.error('[AdminNotifications] Error fetching:', e);
    }
  }, []);

  useEffect(() => {
    // Skip fetching admin notifications for portal and kiosk routes
    if (window.location.pathname.startsWith('/portal') || window.location.pathname.includes('/kiosk')) {
      return;
    }
    
    // Only fetch for admin users
    if (!isAdminUser()) {
      return;
    }
    
    fetchLatest().catch(console.error);
  }, [fetchLatest]);

  const addFromWs = useCallback((payload) => {
    if (!payload) return;
    // Only process WS notifications for admin users
    if (!isAdminUser()) return;
    
    setItems((prev) => {
      // avoid duplicates if reconnect replays
      if (payload._id && prev.some((p) => p._id === payload._id)) return prev;
      const n = {
        _id: payload._id,
        title: payload.title,
        body: payload.body,
        url: payload.url,
        createdAt: payload.createdAt || new Date().toISOString(),
        isRead: false,
        event: payload.event,
        type: "booking",
      };
      return [n, ...prev].slice(0, 200);
    });
  }, []);

  const markRead = useCallback(async (id) => {
    if (!isAdminUser()) return;
    
    setItems((prev) => prev.map((n) => n._id === id ? { ...n, isRead: true } : n));
    try {
      await apiCall.post(`/admin-notifications/${id}/read`);
    } catch (e) {
      console.error(e);
      // rollback if needed
      setItems((prev) => prev.map((n) => n._id === id ? { ...n, isRead: false } : n));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    if (!isAdminUser()) return;
    
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await apiCall.post("/admin-notifications/read-all");
    } catch (e) {
      console.error('[AdminNotifications] Error marking all read:', e);
    }
  }, []);

  const clearAll = useCallback(async () => {
    if (!isAdminUser()) return;
    
    setItems([]);
    try {
      await apiCall.delete("/admin-notifications/clear");
    } catch (e) {
      console.error('[AdminNotifications] Error clearing:', e);
    }
  }, []);

  const value = useMemo(() => ({
    items,
    unreadCount: items.filter((i) => !i.isRead).length,
    addFromWs,
    fetchLatest,
    markRead,
    markAllRead,
    clearAll,
  }), [items, addFromWs, fetchLatest, markRead, markAllRead, clearAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
